/**
 * API 接口层：所有后端接口的封装 + 归一化
 * 页面只从这里调用接口，不直接 fetch
 */
import { apiDelete, apiDownload, apiFetch, apiGet, apiPost, apiPut, apiUpload } from './client'
import { normalizeAuthResponse, normalizeComment, normalizeConversation, normalizeEvent, normalizeLeaderboardEntry, normalizeLeaderboardList, normalizeMatch, normalizeMessage, normalizeNotification, normalizePaged, normalizePlayer, normalizePlayerBinding, normalizePlayerDetail, normalizePlayerEvent, normalizePlayerGameRecord, normalizeRadar, normalizeRuleRevision, normalizeSubscription, normalizeSubscriptionStatus, normalizeTeam, normalizeTeamBlacklist, normalizeTeamEvent, normalizeTeamInvite, normalizeUser, normalizeUserAchievement, normalizeArticle } from './normalize'
import { toBindingType, toEventStatus, toEventType, toPlayerType, toRegistrationMode, toRegistrationStatus, toUserGroup, toUserRole } from './enums'
import type { UserRole, UserGroup, EventStatus, EventType, RegistrationMode, RegistrationStatus, PlayerType } from './enums'
import type { AchievementProgress, Article, AuthResponse, Comment, Conversation, Event, FileListResponse, LeaderboardEntry, LeaderboardStats, Match, Message, Notification, Paged, Player, PlayerBindingRequest, PlayerDetail, PlayerEvent, PlayerGameRecord, PlayerStatsRadar, Recruitment, RecruitmentApplication, RuleRevision, SearchAggregate, Subscription, SubscriptionPlan, SubscriptionStats, Team, TeamBlacklist, TeamBlacklistPublic, TeamEvent, TeamInvite, TeamReview, User, UserAchievement, UserList, UserPrediction, UserSubscriptionStatus, RoleInfo } from './types'

// ==================== Auth ====================

export const authApi = {
  async login(email: string, password: string): Promise<AuthResponse> {
    const r = await apiPost<AuthResponse>('/Auth/login', { email, password })
    return normalizeAuthResponse(r)
  },
  async register(dto: { email: string; password: string; fullName: string; mcaptchaToken?: string }): Promise<AuthResponse> {
    const r = await apiPost<AuthResponse>('/Auth/register', { ...dto, role: '1px' })
    return normalizeAuthResponse(r)
  },
  logout: () => apiPost('/Auth/logout'),
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return apiPost('/Auth/password-reset/request', { email })
  },
  async confirmPasswordReset(email: string, token: string, newPassword: string): Promise<{ message: string }> {
    return apiPost('/Auth/password-reset/confirm', { email, token, newPassword })
  },
}

// ==================== Users ====================

export const usersApi = {
  async me(): Promise<User> {
    const r = await apiGet<User>('/Users/profile')
    return normalizeUser(r)
  },
  async updateProfile(fullName: string): Promise<User> {
    const r = await apiPut<User>('/Users/profile', { fullName })
    return normalizeUser(r)
  },
  async uploadAvatar(file: File): Promise<User> {
    const fd = new FormData()
    fd.append('avatar', file)
    const r = await apiUpload<User>('/Users/avatar', fd)
    return normalizeUser(r)
  },
  async get(id: string): Promise<User> {
    const r = await apiGet<User>(`/Users/${id}`)
    return normalizeUser(r)
  },
  async search(name: string, limit = 10): Promise<User[]> {
    const r = await apiGet<User[]>('/Users/search', { name, limit })
    return r.map(normalizeUser)
  },
  async all(): Promise<User[]> {
    const r = await apiGet<User[]>('/Users')
    return r.map(normalizeUser)
  },
  async remove(id: string): Promise<void> {
    await apiDelete(`/Users/${id}`)
  },
  async updateEmailCredits(id: string, credits: number): Promise<void> {
    await apiPut(`/Users/${id}/email-credits`, { credits })
  },
}

// ==================== Role ====================

export const roleApi = {
  roles: () => apiGet<RoleInfo[]>('/Role/roles'),
  updateRole: (userId: string, role: UserRole) => apiPut<User>('/Role/update-role', { userId, role: toUserRole(role) }).then((r) => normalizeUser(r)),
  usersByRole: (role: UserRole) => apiGet<User[]>(`/Role/users-by-role/${role}`).then((r) => r.map(normalizeUser)),
  users: (pageNumber = 1, pageSize = 10, search?: string) =>
    apiGet<UserList>('/Role/users', { pageNumber, pageSize, search }).then((r) => ({
      ...r,
      users: r.users.map(normalizeUser),
    })),
  statistics: () => apiGet<Record<string, number>>('/Role/statistics'),
  checkPermission: (permission: string) => apiGet<boolean>(`/Role/check-permission/${permission}`),
  myRole: () => apiGet<RoleInfo>('/Role/my-role'),
}

// ==================== Events ====================

export interface CreateEventInput {
  name: string
  description?: string
  qqGroup?: string
  rulesMarkdown?: string
  registrationStartTime: string
  registrationEndTime: string
  competitionStartTime: string
  competitionEndTime?: string
  maxTeams?: number
  waitlistTeams?: number
  registrationMode: RegistrationMode
  prizesJson?: string
  requiredQqGroup?: string
  enforceGroupCheck?: boolean
  requirePlayerProfileScreenshots?: boolean
  eventType: EventType
  mcaptchaToken?: string
}

export interface UpdateEventInput extends CreateEventInput {
  status: EventStatus
}

export const eventsApi = {
  all: () => apiGet<Event[]>('/Events').then((r) => r.map(normalizeEvent)),

  async search(query: string, page = 1, pageSize = 12): Promise<Paged<Event>> {
    const r = await apiGet<{ items: Event[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>('/Events/search', { query, page, pageSize })
    return normalizePaged({ ...r, items: r.items.map(normalizeEvent) })
  },

  get: (id: string) => apiGet<Event>(`/Events/${id}`).then((r) => normalizeEvent(r)),

  create: (dto: CreateEventInput) =>
    apiPost<Event>('/Events', { ...dto, registrationMode: toRegistrationMode(dto.registrationMode), eventType: toEventType(dto.eventType) }).then((r) => normalizeEvent(r)),

  update: (id: string, dto: UpdateEventInput) =>
    apiPut<Event>(`/Events/${id}`, {
      ...dto,
      status: toEventStatus(dto.status),
      registrationMode: toRegistrationMode(dto.registrationMode),
      eventType: toEventType(dto.eventType),
    }).then((r) => normalizeEvent(r)),

  remove: (id: string) => apiDelete(`/Events/${id}`),

  setChampion: (id: string, teamId: string | null) => apiPut<Event>(`/Events/${id}/champion`, { teamId }).then((r) => normalizeEvent(r)),

  uploadLogo: (id: string, file: File) => {
    const fd = new FormData()
    fd.append('logo', file)
    return apiUpload(`/Events/${id}/logo`, fd)
  },
  uploadBackground: (id: string, file: File) => {
    const fd = new FormData()
    fd.append('background', file)
    return apiUpload(`/Events/${id}/background`, fd)
  },

  // 报名
  registrations: (id: string) => apiGet<TeamEvent[]>(`/Events/${id}/registrations`).then((r) => r.map(normalizeTeamEvent)),
  playerRegistrations: (id: string) => apiGet<PlayerEvent[]>(`/Events/${id}/player-registrations`).then((r) => r.map(normalizePlayerEvent)),
  registerTeam: (id: string, teamId: string, notes?: string) =>
    apiPost<TeamEvent>(`/Events/${id}/register`, { teamId, notes }).then((r) => normalizeTeamEvent(r)),
  unregisterTeam: (id: string, teamId: string) => apiDelete(`/Events/${id}/register/${teamId}`),
  updateRegistration: (id: string, teamId: string, status: RegistrationStatus, notes?: string, notifyByEmail = false) =>
    apiPut<TeamEvent>(`/Events/${id}/register/${teamId}`, { status: toRegistrationStatus(status), notes, notifyByEmail }).then((r) => normalizeTeamEvent(r)),
  batchUpdateRegistrations: (id: string, teamIds: string[], status: RegistrationStatus, notes?: string, notifyByEmail = false) =>
    apiPut<TeamEvent[]>(`/Events/${id}/register/batch`, { teamIds, status: toRegistrationStatus(status), notes, notifyByEmail }).then((r) => r.map(normalizeTeamEvent)),
  registerPlayer: (id: string, playerId: string, notes?: string) =>
    apiPost<PlayerEvent>(`/Events/${id}/register-player`, { playerId, notes }).then((r) => normalizePlayerEvent(r)),
  createSoloTeam: (id: string, playerIds: string[], name?: string) =>
    apiPost<TeamEvent>(`/Events/${id}/solo-teams`, { playerIds, name, approveRegistration: true }).then((r) => normalizeTeamEvent(r)),

  // 我的 / 列表
  myEvents: () => apiGet<Event[]>('/Events/my-events').then((r) => r.map(normalizeEvent)),
  createdBy: (userId: string) => apiGet<Event[]>(`/Events/created-by/${userId}`).then((r) => r.map(normalizeEvent)),
  activeRegistration: () => apiGet<Event[]>('/Events/active-registration').then((r) => r.map(normalizeEvent)),
  upcoming: () => apiGet<Event[]>('/Events/upcoming').then((r) => r.map(normalizeEvent)),
  activeRegistrationPaged: (page = 1, pageSize = 12) =>
    apiGet<{ items: Event[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>('/Events/active-registration/paged', { page, pageSize }).then((r) => normalizePaged({ ...r, items: r.items.map(normalizeEvent) })),
  upcomingPaged: (page = 1, pageSize = 12) =>
    apiGet<{ items: Event[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>('/Events/upcoming/paged', { page, pageSize }).then((r) => normalizePaged({ ...r, items: r.items.map(normalizeEvent) })),
  pendingPaged: (page = 1, pageSize = 12) =>
    apiGet<{ items: Event[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>('/Events/pending/paged', { page, pageSize }).then((r) => normalizePaged({ ...r, items: r.items.map(normalizeEvent) })),
  historyPaged: (page = 1, pageSize = 12) =>
    apiGet<{ items: Event[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>('/Events/history/paged', { page, pageSize }).then((r) => normalizePaged({ ...r, items: r.items.map(normalizeEvent) })),
  teamRegistrations: (teamId: string) => apiGet<TeamEvent[]>(`/Events/team/${teamId}/registrations`).then((r) => r.map(normalizeTeamEvent)),

  // 管理员
  admins: (id: string) => apiGet<User[]>(`/Events/${id}/admins`).then((r) => r.map(normalizeUser)),
  addAdmin: (id: string, userId: string) => apiPost(`/Events/${id}/admins`, { userId }),
  removeAdmin: (id: string, adminUserId: string) => apiDelete(`/Events/${id}/admins/${adminUserId}`),
  batchDelete: (ids: string[]) => apiPost('/Events/batch-delete', { ids }),

  // 规则版本
  ruleRevisions: (id: string) => apiGet<RuleRevision[]>(`/Events/${id}/rules/revisions`).then((r) => r.map(normalizeRuleRevision)),
  createRuleRevision: (id: string, contentMarkdown: string, changeNotes?: string) =>
    apiPost<RuleRevision>(`/Events/${id}/rules/revisions`, { contentMarkdown, changeNotes }).then((r) => normalizeRuleRevision(r)),
  publishRuleRevision: (id: string, revId: string) => apiPost(`/Events/${id}/rules/revisions/${revId}/publish`),

  // 报名表
  registrationFormSchema: (id: string) => apiGet<{ schemaJson?: string }>(`/Events/${id}/registration-form-schema`),
  updateRegistrationFormSchema: (id: string, schemaJson: string) => apiPut(`/Events/${id}/registration-form-schema`, { schemaJson }),

  // 赛制配置
  roundsConfig: (id: string) => apiGet<unknown[]>(`/Events/${id}/rounds-config`),
  updateRoundsConfig: (id: string, rounds: unknown[]) => apiPut(`/Events/${id}/rounds-config`, { rounds }),
  updateTournamentConfig: (id: string, config: unknown) => apiPut(`/Events/${id}/tournament-config`, config),

  generateTestRegistrations: (id: string, count = 64, namePrefix?: string) =>
    apiPost<TeamEvent[]>(`/Events/${id}/generate-test-registrations`, { count, namePrefix, approve: true }).then((r) => r.map(normalizeTeamEvent)),

  // 导出
  exportRegistrations: (id: string, kind: 'team' | 'solo', fmt: 'csv' | 'excel') =>
    apiDownload(`/Events/${id}/registrations/${kind === 'team' ? '' : 'solo/'}export${fmt === 'excel' ? '-excel' : ''}`),
}

// ==================== Teams ====================

export interface CreateTeamInput {
  name: string
  password: string
  description?: string
  qqNumber: string
  players: CreatePlayerInput[]
  hidePlayers?: boolean
}

export interface CreatePlayerInput {
  name: string
  gameId?: string
  gameRank?: string
  description?: string
  playerType?: PlayerType
  xuexinVerificationCode?: string
}

export const teamsApi = {
  async list(page = 1, pageSize = 12): Promise<Paged<Team>> {
    const r = await apiGet<{ items: Team[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>('/Teams', { page, pageSize })
    return normalizePaged({ ...r, items: r.items.map(normalizeTeam) })
  },
  async search(name: string, page = 1, pageSize = 12): Promise<Paged<Team>> {
    const r = await apiGet<{ items: Team[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>('/Teams/search', { name, page, pageSize })
    return normalizePaged({ ...r, items: r.items.map(normalizeTeam) })
  },
  get: (id: string) => apiGet<Team>(`/Teams/${id}`).then((r) => normalizeTeam(r)),
  register: (dto: CreateTeamInput) =>
    apiPost<Team>('/Teams/register', {
      ...dto,
      players: dto.players.map((p) => ({ ...p, playerType: p.playerType ? toPlayerType(p.playerType) : null })),
    }).then((r) => normalizeTeam(r)),
  update: (id: string, dto: { name: string; description?: string; qqNumber?: string; hidePlayers?: boolean; players: unknown[] }) =>
    apiPut<Team>(`/Teams/${id}`, dto).then((r) => normalizeTeam(r)),
  remove: (id: string) => apiDelete(`/Teams/${id}`),
  honors: (id: string) => apiGet<Event[]>(`/Teams/${id}/honors`).then((r) => r.map(normalizeEvent)),
  uploadLogo: (id: string, file: File) => {
    const fd = new FormData()
    fd.append('logo', file)
    return apiUpload(`/Teams/${id}/logo`, fd)
  },
  uploadLogoFromUrl: (id: string, url: string) => apiPost(`/Teams/${id}/logo-from-url`, { url }),
  myPlayer: () => apiGet<Player>('/Teams/me/player').then((r) => (r ? normalizePlayer(r) : r)),
  createMyPlayer: (dto: CreatePlayerInput) =>
    apiPost<Player>('/Teams/me/player', { ...dto, playerType: dto.playerType ? toPlayerType(dto.playerType) : null }).then((r) => normalizePlayer(r)),
  invite: (id: string, validDays = 7) => apiGet<TeamInvite>(`/Teams/${id}/invite`, { validDays }).then((r) => normalizeTeamInvite(r)),
  getInvite: (token: string) => apiGet<TeamInvite>(`/Teams/invites/${token}`).then((r) => normalizeTeamInvite(r)),
  acceptInvite: (token: string, player?: CreatePlayerInput) =>
    apiPost<Player>(`/Teams/invites/${token}/accept`, player ? { ...player, playerType: player.playerType ? toPlayerType(player.playerType) : null } : null).then((r) => normalizePlayer(r)),
  leave: (id: string) => apiPost(`/Teams/${id}/leave`),
  bind: (teamId: string, password: string) => apiPost('/Teams/bind', { teamId, password }),
  bindByName: (name: string, password: string) => apiPost('/Teams/bind-by-name', { name, password }),
  unbind: () => apiPost('/Teams/unbind'),
  changePassword: (id: string, currentPassword: string, newPassword: string) => apiPost(`/Teams/${id}/change-password`, { currentPassword, newPassword }),
  transferOwner: (id: string, targetUserId: string) => apiPost(`/Teams/${id}/transfer-owner`, { targetUserId }),
  verifyOwnership: (id: string) => apiGet<boolean>(`/Teams/${id}/verify-ownership`),
  like: (id: string) => apiPost(`/Teams/${id}/like`, {}),
  reviews: (id: string) => apiGet<TeamReview[]>(`/Teams/${id}/reviews`),
  setDispute: (id: string, hasDispute: boolean, disputeDetail?: string, communityPostId?: string) =>
    apiPost(`/Teams/${id}/dispute`, { hasDispute, disputeDetail, communityPostId }),
  batchDelete: (ids: string[]) => apiPost('/Teams/batch-delete', { ids }),
}

// ==================== Matches ====================

export interface GenerateScheduleInput {
  stage?: string
  round?: number
  bestOf?: number
  startTime?: string
  intervalMinutes?: number
  startDate?: string
  dailyStartTime?: string
  maxMatchesPerDay?: number
}

export const matchesApi = {
  list: (eventId?: string, page = 1, pageSize = 200, groupIndex?: number, groupLabel?: string) =>
    apiGet<Match[]>('/Matches', { eventId, page, pageSize, groupIndex, groupLabel }).then((r) => r.map(normalizeMatch)),
  get: (id: string) => apiGet<Match>(`/Matches/${id}`).then((r) => normalizeMatch(r)),
  create: (dto: { homeTeamId: string; awayTeamId: string; matchTime: string; eventId: string; liveLink?: string; commentator?: string; director?: string; referee?: string }) =>
    apiPost<Match>('/Matches', dto).then((r) => normalizeMatch(r)),
  update: (id: string, dto: Record<string, unknown>) => apiPut<Match>(`/Matches/${id}`, dto).then((r) => normalizeMatch(r)),
  updateScores: (id: string, bestOf: number | null, games: { home: number; away: number }[]) =>
    apiPut<Match>(`/Matches/${id}/scores`, { bestOf, games }).then((r) => normalizeMatch(r)),
  remove: (id: string) => apiDelete(`/Matches/${id}`),
  generateSchedule: (eventId: string, dto: GenerateScheduleInput) =>
    apiPost<Match[]>(`/Matches/generate-schedule/${eventId}`, dto).then((r) => r.map(normalizeMatch)),
  generateNextRound: (eventId: string, dto: GenerateScheduleInput) =>
    apiPost<Match[]>(`/Matches/next-round/${eventId}`, dto).then((r) => r.map(normalizeMatch)),
  generateRound: (eventId: string, round: number, dto: GenerateScheduleInput) =>
    apiPost<Match[]>(`/Matches/generate-round/${eventId}/${round}`, dto).then((r) => r.map(normalizeMatch)),
  validateRound: (eventId: string, round: number) => apiGet<{ isValid: boolean; errorMessage?: string; warnings: string[] }>(`/Matches/validate-round/${eventId}/${round}`),
  previewRound: (eventId: string, round: number) => apiGet<{ isValid: boolean; errorMessage?: string; pairs: unknown[] }>(`/Matches/preview-round/${eventId}/${round}`),
  conflicts: (eventId: string) => apiGet<unknown[]>(`/Matches/conflicts/${eventId}`),
  exportCsv: (eventId: string) => apiDownload(`/Matches/export/${eventId}`),
  exportExcel: (eventId: string) => apiDownload(`/Matches/export-excel/${eventId}`),
  like: (id: string) => apiPost<number>(`/Matches/${id}/like`, {}),
  batchDelete: (ids: string[]) => apiPost('/Matches/batch-delete', { ids }),
}

// ==================== Articles ====================

export const articlesApi = {
  list: (page = 1, pageSize = 10, sortBy?: string, desc = true, query?: string) =>
    apiGet<{ items: Article[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>('/Articles', { page, pageSize, sortBy, desc, query }).then((r) => normalizePaged({ ...r, items: r.items.map(normalizeArticle) })),
  search: (query: string, page = 1, pageSize = 10) =>
    apiGet<{ items: Article[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>('/Articles/search', { query, page, pageSize }).then((r) => normalizePaged({ ...r, items: r.items.map(normalizeArticle) })),
  get: (id: string) => apiGet<Article>(`/Articles/${id}`).then((r) => normalizeArticle(r)),
  create: (title: string, contentMarkdown: string) => apiPost<Article>('/Articles', { title, contentMarkdown }).then((r) => normalizeArticle(r)),
  update: (id: string, title: string, contentMarkdown: string) => apiPut<Article>(`/Articles/${id}`, { title, contentMarkdown }).then((r) => normalizeArticle(r)),
  remove: (id: string) => apiDelete(`/Articles/${id}`),
  comments: (id: string, page = 1, pageSize = 20) =>
    apiGet<{ items: Comment[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>(`/Articles/${id}/comments`, { page, pageSize }).then((r) => normalizePaged({ ...r, items: r.items.map(normalizeComment) })),
  addComment: (id: string, content: string, parentId?: string) => apiPost<Comment>(`/Articles/${id}/comments`, { content, parentId }).then((r) => normalizeComment(r)),
  like: (id: string) => apiPost<number>(`/Articles/${id}/like`, {}),
  view: (id: string) => apiPost<number>(`/Articles/${id}/view`),
}

// ==================== Search ====================

export const searchApi = {
  async all(q: string, page = 1, pageSize = 12): Promise<SearchAggregate> {
    const r = await apiGet<{
      teams?: { items: Team[]; totalCount: number; page: number; pageSize: number; totalPages?: number } | null
      events?: { items: Event[]; totalCount: number; page: number; pageSize: number; totalPages?: number } | null
      articles?: { items: Article[]; totalCount: number; page: number; pageSize: number; totalPages?: number } | null
    }>('/Search', { q, type: 'all', page, pageSize })
    return {
      teams: r.teams ? normalizePaged({ ...r.teams, items: r.teams.items.map(normalizeTeam) }) : null,
      events: r.events ? normalizePaged({ ...r.events, items: r.events.items.map(normalizeEvent) }) : null,
      articles: r.articles ? normalizePaged({ ...r.articles, items: r.articles.items.map(normalizeArticle) }) : null,
    }
  },
  teams: (q: string, page = 1, pageSize = 12) =>
    apiGet<{ items: Team[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>('/Search', { q, type: 'teams', page, pageSize }).then((r) => normalizePaged({ ...r, items: r.items.map(normalizeTeam) })),
  events: (q: string, page = 1, pageSize = 12) =>
    apiGet<{ items: Event[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>('/Search', { q, type: 'events', page, pageSize }).then((r) => normalizePaged({ ...r, items: r.items.map(normalizeEvent) })),
  articles: (q: string, page = 1, pageSize = 12) =>
    apiGet<{ items: Article[]; totalCount: number; page: number; pageSize: number; totalPages?: number }>('/Search', { q, type: 'articles', page, pageSize }).then((r) => normalizePaged({ ...r, items: r.items.map(normalizeArticle) })),
}

// ==================== Leaderboard ====================

export const leaderboardApi = {
  list: (type = 'overall', page = 1, pageSize = 50) =>
    apiGet<LeaderboardEntry[]>('/Leaderboard', { type, page, pageSize }).then((r) => normalizeLeaderboardList(r.map(normalizeLeaderboardEntry))),
  me: (type = 'overall') => apiGet<LeaderboardEntry>('/Leaderboard/me', { type }).then((r) => (r ? normalizeLeaderboardEntry(r) : r)),
  user: (userId: string, type = 'overall') => apiGet<LeaderboardEntry>(`/Leaderboard/user/${userId}`, { type }).then((r) => (r ? normalizeLeaderboardEntry(r) : r)),
  stats: (type = 'overall') => apiGet<LeaderboardStats>('/Leaderboard/stats', { type }),
  refresh: (type = 'overall') => apiPost('/Leaderboard/refresh', undefined, { params: { type } }),
}

// ==================== Notifications ====================

export const notificationsApi = {
  list: () => apiGet<Notification[]>('/Notifications').then((r) => r.map(normalizeNotification)),
  read: (id: string) => apiPost(`/Notifications/${id}/read`),
  readAll: () => apiPost('/Notifications/read-all'),
  publish: (dto: { userId: string; type: string; payload: string }) => apiPost('/Notifications/publish', dto),
}

// ==================== Messages ====================

export const messagesApi = {
  conversations: () => apiGet<Conversation[]>('/Messages/conversations').then((r) => r.map(normalizeConversation)),
  messages: (conversationId: string) => apiGet<Message[]>(`/Messages/conversations/${conversationId}/messages`).then((r) => r.map(normalizeMessage)),
  markRead: (conversationId: string) => apiPost(`/Messages/conversations/${conversationId}/read`),
  send: (toUserId: string, content: string) => apiPost<Message>(`/Messages/user/${toUserId}/message`, { content }).then((r) => normalizeMessage(r)),
  clear: (conversationId: string) => apiDelete(`/Messages/conversations/${conversationId}/clear`),
  block: (blockedUserId: string) => apiPost(`/Messages/blocks/${blockedUserId}`),
  unblock: (blockedUserId: string) => apiDelete(`/Messages/blocks/${blockedUserId}`),
  checkBlock: (peerUserId: string) => apiGet<{ blocked?: boolean; isBlocked?: boolean }>(`/Messages/blocks/${peerUserId}`),
}

// ==================== Achievements ====================

export interface AchievementDef {
  id: string
  code: string
  name: string
  description: string
  icon: string
  category: string
  rarity: string
  points: number
}

export const achievementsApi = {
  all: () => apiGet<AchievementDef[]>('/Achievements'),
  me: () => apiGet<UserAchievement[]>('/Achievements/me').then((r) => r.map(normalizeUserAchievement)),
  byUser: (userId: string) => apiGet<UserAchievement[]>(`/Achievements/user/${userId}`).then((r) => r.map(normalizeUserAchievement)),
  newUnlocked: () => apiGet<UserAchievement[]>('/Achievements/new').then((r) => r.map(normalizeUserAchievement)),
  progress: () => apiGet<AchievementProgress>('/Achievements/progress'),
}

// ==================== GameRecords ====================

export const gameRecordsApi = {
  myRadar: () => apiGet<PlayerStatsRadar>('/GameRecords/me/stats-radar').then((r) => (r ? normalizeRadar(r) : r)),
  playerRadar: (playerId: string) => apiGet<PlayerStatsRadar>(`/GameRecords/players/${playerId}/stats-radar`).then((r) => (r ? normalizeRadar(r) : r)),
  playerRecords: (playerId: string) => apiGet<PlayerGameRecord[]>(`/GameRecords/players/${playerId}/records`).then((r) => r.map(normalizePlayerGameRecord)),
  teamStats: (teamId: string) => apiGet<PlayerStatsRadar[]>(`/GameRecords/teams/${teamId}/stats`).then((r) => r.map(normalizeRadar)),
  playersSearch: (keyword: string, limit = 20) => apiGet<Player[]>(`/GameRecords/players/search`, { keyword, limit }).then((r) => r.map(normalizePlayer)),
  byMatch: (matchId: string) => apiGet<unknown[]>(`/GameRecords/match/${matchId}`),
  create: (dto: { matchId: string; gameNumber?: number; mapName?: string; notes?: string; gameTime?: string }) => apiPost('/GameRecords', dto),
  remove: (id: string) => apiDelete(`/GameRecords/${id}`),
  addPlayerRecord: (dto: { gameRecordId: string; playerId: string; teamId: string; playerType: string; roleName?: string; score?: number }) =>
    apiPost('/GameRecords/player-records', dto),
  removePlayerRecord: (id: string) => apiDelete(`/GameRecords/player-records/${id}`),
}

// ==================== Subscriptions ====================

export const subscriptionsApi = {
  plans: () => apiGet<SubscriptionPlan[]>('/Subscriptions/plans'),
  thanksList: () => apiGet<{ userName: string; tierDisplayName: string; tier: number }[]>('/Subscriptions/thanks-list'),
  status: () => apiGet<UserSubscriptionStatus>('/Subscriptions/status').then((r) => normalizeSubscriptionStatus(r)),
  history: () => apiGet<Subscription[]>('/Subscriptions/history').then((r) => r.map(normalizeSubscription)),
  stats: () => apiGet<SubscriptionStats>('/Subscriptions/stats'),
  list: (page = 1, pageSize = 10, search?: string) =>
    apiGet<{ subscriptions: Subscription[]; totalCount: number; page: number; pageSize: number }>('/Subscriptions', { page, pageSize, search }).then((r) => ({
      ...r,
      subscriptions: r.subscriptions.map(normalizeSubscription),
    })),
  byUser: (userId: string) => apiGet<Subscription[]>('/Subscriptions/user/' + userId).then((r) => r.map(normalizeSubscription)),
  create: (userId: string, tier: UserGroup, months = 1, amount = 0, adminNote?: string) =>
    apiPost<Subscription>('/Subscriptions', { userId, tier: toUserGroup(tier), months, amount, adminNote }).then((r) => normalizeSubscription(r)),
  updateUserGroup: (userId: string, group: UserGroup, expireTime?: string, adminNote?: string) =>
    apiPut(`/Subscriptions/user/${userId}/group`, { group: toUserGroup(group), expireTime, adminNote }),
  cancel: (id: string, adminNote?: string) =>
    apiFetch(`/Subscriptions/${id}`, { method: 'DELETE', body: { adminNote } }),
}

// ==================== Blacklists ====================

export const blacklistsApi = {
  public: (limit = 100) => apiGet<TeamBlacklistPublic[]>('/Blacklists/public', { limit }),
  ban: (teamId: string, reason?: string) => apiPost<TeamBlacklist>(`/Blacklists/teams/${teamId}/ban`, { reason }).then((r) => normalizeTeamBlacklist(r)),
  updateBan: (teamId: string, reason?: string) => apiPut<TeamBlacklist>(`/Blacklists/teams/${teamId}/ban`, { reason }).then((r) => normalizeTeamBlacklist(r)),
  unban: (teamId: string) => apiDelete(`/Blacklists/teams/${teamId}/ban`),
}

// ==================== PlayerBinding ====================

export const playerBindingApi = {
  createRequest: (playerId: string, targetUserIdentifier: string, message?: string, validDays = 7) =>
    apiPost<PlayerBindingRequest>('/PlayerBinding/requests', { playerId, targetUserIdentifier, message, validDays }).then((r) => normalizePlayerBinding(r)),
  createTransfer: (playerId: string, targetUserIdentifier: string, message?: string) =>
    apiPost<PlayerBindingRequest>('/PlayerBinding/transfer', { playerId, targetUserIdentifier, message }).then((r) => normalizePlayerBinding(r)),
  process: (requestId: string, accept: boolean, message?: string) =>
    apiPost<PlayerBindingRequest>(`/PlayerBinding/requests/${requestId}/process`, { accept, message }).then((r) => normalizePlayerBinding(r)),
  cancel: (requestId: string) => apiPost(`/PlayerBinding/requests/${requestId}/cancel`),
  get: (requestId: string) => apiGet<PlayerBindingRequest>(`/PlayerBinding/requests/${requestId}`).then((r) => normalizePlayerBinding(r)),
  pending: () => apiGet<PlayerBindingRequest[]>('/PlayerBinding/requests/pending').then((r) => r.map(normalizePlayerBinding)),
  initiated: (includeProcessed = false) =>
    apiGet<PlayerBindingRequest[]>('/PlayerBinding/requests/initiated', { includeProcessed }).then((r) => r.map(normalizePlayerBinding)),
  history: (playerId: string) => apiGet<PlayerBindingRequest[]>(`/PlayerBinding/players/${playerId}/history`).then((r) => r.map(normalizePlayerBinding)),
  unbind: (playerId: string) => apiPost('/PlayerBinding/unbind', { playerId }),
  teamPlayers: (teamId: string) => apiGet<PlayerDetail[]>(`/PlayerBinding/teams/${teamId}/players`).then((r) => r.map(normalizePlayerDetail)),
  playerDetail: (playerId: string) => apiGet<PlayerDetail>(`/PlayerBinding/players/${playerId}`).then((r) => normalizePlayerDetail(r)),
  canManage: (playerId: string) => apiGet<boolean>(`/PlayerBinding/players/${playerId}/can-manage`),
  batch: (dto: { playerIds: string[]; action: string; targetTeamId?: string }) => apiPost('/PlayerBinding/batch', dto),
}

// ==================== Predictions ====================

export const predictionsApi = {
  create: (matchId: string, predictedTeamId: string) =>
    apiPost('/Predictions', { matchId, predictedTeamId }),
  stats: (matchId: string) => apiGet<import('./types').PredictionStats>(`/Predictions/match/${matchId}/stats`),
  my: (matchId: string) => apiGet<UserPrediction | null>(`/Predictions/match/${matchId}/my`),
  myAll: () => apiGet<UserPrediction[]>('/Predictions/my'),
  remove: (matchId: string) => apiDelete(`/Predictions/match/${matchId}`),
  canPredict: (matchId: string) => apiGet<{ canPredict: boolean }>(`/Predictions/match/${matchId}/can-predict`),
  eventStats: (eventId: string) => apiGet<import('./types').PredictionStats[]>(`/Predictions/event/${eventId}/stats`),
}

// ==================== Recruitments ====================

export const recruitmentsApi = {
  list: (params?: { eventId?: string; position?: string; q?: string; includeClosed?: boolean }) =>
    apiGet<Recruitment[]>('/Recruitments', params),
  get: (id: string) => apiGet<Recruitment>(`/Recruitments/${id}`),
  create: (dto: { title: string; eventId: string; positionType: string; payPerMatch: number; slots: number; description?: string }) =>
    apiPost<Recruitment>('/Recruitments', dto),
  update: (id: string, dto: Record<string, unknown>) => apiPut<Recruitment>(`/Recruitments/${id}`, dto),
  remove: (id: string) => apiDelete(`/Recruitments/${id}`),
  applications: (id: string) => apiGet<RecruitmentApplication[]>(`/Recruitments/${id}/applications`),
  apply: (id: string, message?: string) => apiPost<RecruitmentApplication>(`/Recruitments/${id}/apply`, { message }),
  addStaff: (dto: { eventId: string; userId: string; positionType: string }) => apiPost('/Recruitments/staff', dto),
  removeStaff: (id: string) => apiDelete(`/Recruitments/staff/${id}`),
  eventStaff: (eventId: string) => apiGet<unknown[]>(`/Recruitments/events/${eventId}/staff`),
}

// ==================== Reminders ====================

export const remindersApi = {
  create: (matchId: string, minutesBefore = 15, channel = 'push') => apiPost('/Reminders', { matchId, minutesBefore, channel }),
  list: () => apiGet<{ id: string; matchId: string; matchTitle: string; scheduledTime?: string; minutesBefore: number; channel: string }[]>('/Reminders'),
  remove: (id: string) => apiDelete(`/Reminders/${id}`),
  countdown: (matchId: string) => apiGet<unknown>(`/Reminders/countdown/${matchId}`),
}

// ==================== Admin: Audit / System / Files ====================

export const auditApi = {
  logs: () => apiGet<{ name: string; size: number; lastModified: string }[]>('/Audit/logs'),
  logContent: (name: string, q?: string, limit = 500, offset = 0) =>
    apiGet<{ name: string; lines: string[]; hasMore: boolean }>('/Audit/logs/content', { name, q, limit, offset }),
}

export const systemApi = {
  metrics: () => apiGet<Record<string, unknown>>('/System/metrics'),
  processes: () => apiGet<unknown[]>('/System/processes'),
}

export const filesApi = {
  list: (path?: string) => apiGet<FileListResponse>('/Files/list', { path }),
  download: (path: string) => apiDownload('/Files/download', undefined, { path }),
  upload: (file: File, path?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    if (path) fd.append('path', path)
    return apiUpload('/Files/upload', fd)
  },
  mkdir: (path: string) => apiPost('/Files/mkdir', { path }),
  rename: (oldPath: string, newPath: string) => apiPost('/Files/rename', { oldPath, newPath }),
  remove: (path: string) => apiFetch('/Files/delete', { method: 'DELETE', params: { path } }),
}

// ==================== Games roles / misc ====================

export const gameRolesApi = {
  list: () => apiGet<{ id: string; name: string }[]>('/GameRoles'),
}

export { toBindingType }
